import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import useTranslationHook from '../../../../hooks/useTranslationHook';
import { updateBuyerRegistrationField } from '../../../../../redux/slice/buyerSlice';
import { BUYER_REGISTRATION_STEPS } from './constant';
import BidderRegistrationAgreementModal from '../modals/BidderRegistrationAgreementModal';
import { getLotFinancials } from '../../../../../utils';
import { formatNumberByLocale } from '../../../../../helpers';
import { handleSelection } from '../../../../../redux/slice/liveAuctionSlice';
import { handleAuctionBidRegistration, bidLimitIncrement } from '../../../../../redux/action/buyerAction';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';

const BiddingLimit = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const lots = useSelector((state) => state.liveAuction.lots);
    const { t } = useTranslationHook();
    const location = useLocation();
    const { selectedLots, isBidderRegistrationModalOpen } = useSelector(
        (state) => state.buyer.buyerRegistration
    );
    const [nextBidAmount, setNextBidAmount] = useState(0);
    const registration_id = useSelector(
        (state) => state.buyer.buyerRegistration.registration_id
    );
    const selectedLot = useSelector((state) => state.liveAuction.selectedLot);
    const liveBiddingLimit = useSelector((state) => state.liveAuction.bidding_limit);
    const liveDepositAmount = useSelector((state) => state.liveAuction.deposit_amount);

    const changeInterval = parseInt(1000);
    const data = lots?.data ?? [];
    const { selectedSessionID, isBidLimitIncrease } = location?.state || {};
    const BidMultiplier = lots?.data?.[0]?.bid_limit_multiplier ?? 10;

    const { biddingLimit, minRequiredDeposit } = useMemo(() => {
        return getLotFinancials(data, selectedLots, BidMultiplier, {
            bidding_limit: liveBiddingLimit,
            deposit_amount: liveDepositAmount
        });
    }, [data, selectedLots]);

    const lang = useSelector((state) => state.translation.lang);

    const onBidChange = useCallback((op) => {
        setNextBidAmount((prev) => {
            let next = prev + (op === "increase" ? changeInterval : -changeInterval);
            let deposit = Math.ceil(next / BidMultiplier);
            if (deposit < minRequiredDeposit) {
                toast.dismiss("min_bid_toast");
                toast.info(t("You cannot go below the minimum bidding limit of {{amount}}", {
                    amount: formatNumberByLocale(biddingLimit, lang),
                }), { toastId: "min_bid_toast" });
                deposit = minRequiredDeposit;
                next = deposit * BidMultiplier;
            }
            return next;
        });
    }, [minRequiredDeposit, changeInterval, t, lang, BidMultiplier]);

    const depositRequired = useMemo(() => {
        const calculated = Math.ceil(nextBidAmount / BidMultiplier);
        return Math.max(calculated, minRequiredDeposit);
    }, [nextBidAmount, minRequiredDeposit]);

    const setBuyerField = useCallback(
        (field, value) => dispatch(updateBuyerRegistrationField({ field, value })), []
    );

    useEffect(() => { setNextBidAmount(biddingLimit); }, [biddingLimit]);

    const currentBidLimit = selectedLot?.available_bid_limit ?? lots?.data?.[0]?.available_bid_limit ?? 0;
    const currentDeposit = Math.round(currentBidLimit / BidMultiplier);

    const [additionalAmount, setAdditionalAmount] = useState(0);

    const onDecrease = useCallback(() => {
        setAdditionalAmount(prev => Math.max(0, prev - changeInterval));
    }, [changeInterval]);

    const onIncrease = useCallback(() => {
        setAdditionalAmount(prev => prev + changeInterval);
    }, [changeInterval]);

    const displayDeposit = currentDeposit + additionalAmount;
    const displayBidLimit = displayDeposit * BidMultiplier;

    const onIncreaseLimitClick = async () => {
        const response = await dispatch(bidLimitIncrement({
            session_id: selectedSessionID,
            requested_deposit: additionalAmount,
        }));
        const { code, error, msg, data } = response || {};
        if (code !== 1 || error !== 0) {
            toast.info(msg || "Something went wrong");
            return;
        }
        toast.success(msg);
        dispatch(handleSelection({
            deposit_amount: additionalAmount,
            bidding_limit: additionalAmount * BidMultiplier,
            ...(data?.bid_transaction_id && { bid_transaction_id: data?.bid_transaction_id })
        }));
        goToNextStep();
    };

    const goToNextStep = () => setBuyerField('isBidderRegistrationModalOpen', true);

    const onNextBtnClick = async () => {
        const payload = {
            registration_id,
            auction_session: selectedSessionID,
            lots: selectedLots,
            deposit_amount: depositRequired,
            bid_limit: nextBidAmount,
            step: 3
        };
        dispatch(handleSelection({ deposit_amount: depositRequired, bidding_limit: nextBidAmount }));
        const response = await dispatch(handleAuctionBidRegistration(payload));
        const { code, error, msg } = response || {};
        if (code !== 1 || error !== 0) {
            toast.info(msg || "Something went wrong");
            return;
        }
        toast.success(msg);
        goToNextStep();
    };

    return (
        <div className={`step-3${isBidLimitIncrease ? ' bid-increase-wrap' : ''}`}>
            <section className="lot-progress">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 col-md-10 col-sm-12">
                            <h5>
                                <span>
                                    {isBidLimitIncrease ? t("STEP 1/2") : t("STEP 3/4")}
                                    <circle></circle>
                                    {isBidLimitIncrease ? t("Increase Bidding Limit") : t("Registering for Live Auction Name")}
                                </span>
                                {t("Bidding Limit")}
                                <em>{t("Adjust your bidding limit to increase your bidding power.")}</em>
                            </h5>
                            {isBidLimitIncrease ? (
                                <ul className="two-step">
                                    <li className="step"></li>
                                    <li></li>
                                </ul>
                            ) : (
                                <ul>
                                    <li className="step active"></li>
                                    <li className='step active'></li>
                                    <li className='step'></li>
                                    <li></li>
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section className="lot-form">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 col-md-10 col-sm-12">
                            <div className="register-for-someone">

                                {isBidLimitIncrease ? (
                                    /* ── Increase flow inner card ── */
                                    <div className="increase-card">
                                        <div className="increase-limit-card">
                                            <div className="increase-limit-row">
                                                <button
                                                    type="button"
                                                    className={additionalAmount === 0 ? "circle-light" : "circle"}
                                                    onClick={onDecrease}
                                                    disabled={additionalAmount === 0}
                                                >
                                                    <img src="/img/minus.svg" alt="" />
                                                </button>
                                                <div className="increase-limit-center">
                                                    <span className="amt-label">{t("DEPOSIT")}</span>
                                                    <h2>
                                                        <em className="dirham">D</em>{" "}
                                                        {formatNumberByLocale(displayDeposit, lang)}
                                                    </h2>
                                                    <span className={`amt-change${additionalAmount > 0 ? ' green' : ''}`}>
                                                        + <em className="dirham">D</em>{" "}
                                                        {formatNumberByLocale(additionalAmount, lang)}
                                                    </span>
                                                </div>
                                                <button type="button" className="circle" onClick={onIncrease}>
                                                    <img src="/img/plus-icon.svg" alt="" />
                                                </button>
                                            </div>
                                            <div className="increase-limit-divider"></div>
                                            <div className="increase-limit-bid-row">
                                                <span className="bid-up-label">{t("BID UP TO")}</span>
                                                <span className="bid-up-amount">
                                                    <em className="dirham">D</em>{" "}
                                                    {formatNumberByLocale(displayBidLimit, lang)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="bidding-rule">
                                            <h6><img src="/img/info-icon.svg" alt="" /> {t("Bidding Rules")}</h6>
                                            <ul>
                                                <li>{t("You can bid on multiple properties, as long as your total active bids stay within your limit.")}</li>
                                                <li>{t("Your deposit sets your bidding limit. The more you deposit, the more bidding power you have")}</li>
                                            </ul>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="chosse-price">
                                            <div className="block">
                                                <div className="price-calculator-box">
                                                    <button className='circle' onClick={() => onBidChange("decrease")}>
                                                        <img src="/img/minus.svg" alt="" />
                                                    </button>
                                                    <h3>
                                                        <span>{t("YOUR BIDDING LIMIT")}</span>
                                                        <em className='dirham'>D</em> {t("Amount", { amount: formatNumberByLocale(nextBidAmount, lang) })}
                                                    </h3>
                                                    <button className='circle' onClick={() => onBidChange("increase")}>
                                                        <img src="/img/plus-icon.svg" alt="" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="block">
                                                <p className='mb-2'>
                                                    {t("Deposit required")} &nbsp;
                                                    <strong>
                                                        <em className='dirham'>D</em> {t("Amount", { amount: formatNumberByLocale(depositRequired, lang) })}
                                                    </strong>
                                                </p>
                                                {!selectedLots.length ? <></> :
                                                    <p className='mb0'>
                                                        {t("Minimum Deposit required")}&nbsp;
                                                        <strong><em className='dirham'>D</em> {t("Amount", { amount: formatNumberByLocale(minRequiredDeposit, lang) })}</strong>
                                                    </p>
                                                }
                                            </div>
                                        </div>
                                        <div className="bidding-rule">
                                            <h6><img src="/img/info-icon.svg" alt="" /> {t("Bidding Rules")}</h6>
                                            <ul>
                                                <li>{t("You can bid on multiple properties, as long as your total active bids stay within your limit.")}</li>
                                                <li>{t("Your deposit sets your bidding limit. The more you deposit, the more bidding power you have")}</li>
                                            </ul>
                                        </div>
                                    </>
                                )}

                            </div>

                            <div className="bidder-btns mt-4">
                                <button
                                    type="button"
                                    className="btn btn-sky back-btn"
                                    onClick={isBidLimitIncrease
                                        ? () => navigate(-1)
                                        : () => setBuyerField('step', BUYER_REGISTRATION_STEPS.LOTS_SELECTION)
                                    }
                                >
                                    <img src="/img/arrow-left.svg" alt="" className="mr4" />{t("Back")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary forward-btn"
                                    onClick={isBidLimitIncrease ? onIncreaseLimitClick : onNextBtnClick}
                                    disabled={isBidLimitIncrease && additionalAmount === 0}
                                >
                                    {t("Next")}<img src="/img/arrow-right.svg" alt="" className="ml4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {isBidderRegistrationModalOpen &&
                <BidderRegistrationAgreementModal
                    show={isBidderRegistrationModalOpen}
                    onClose={() => setBuyerField('isBidderRegistrationModalOpen', false)}
                    proceedToPaymentStep={() => {
                        setBuyerField('step', BUYER_REGISTRATION_STEPS.PAYMENT);
                        setBuyerField('isBidderRegistrationModalOpen', false);
                    }}
                />
            }
        </div>
    );
};

export default BiddingLimit;
